import type { GraphNode, NodeData, StepMemory } from '@/lib/workflows'
import { FALLIBLE, WEEKDAYS } from '@/lib/workflows'
import { cn } from '@/lib/utils'
import { Copy, Plus, Trash2, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { AgentAvatar } from '../AgentAvatar'
import { TYPE_META, field, fieldLabel, nodeTitle, useCatalog } from './shared'

interface Props {
  node: GraphNode
  /** Earlier steps, for the {{id}} hints. */
  earlier: GraphNode[]
  readOnly: boolean
  /** Webhook triggers: the full address other apps call (empty until saved). */
  hookUrl?: string
  /** What this step remembers between runs (smart checks: accuracy and the last check). */
  memory?: StepMemory
  onChange: (patch: Partial<NodeData>) => void
  onDelete: () => void
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="block">
      <span className={fieldLabel}>{label}</span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] leading-relaxed text-neutral-400">{hint}</span>
      )}
    </label>
  )
}

type Plain = string | number | boolean

/** Name → value rows (headers, record fields). Order is kept while names are edited. */
function Pairs({
  value,
  onChange,
  keyHint,
  valueHint
}: {
  value: Record<string, Plain>
  onChange: (next: Record<string, Plain>) => void
  keyHint: string
  valueHint: string
}) {
  const rows = Object.entries(value)
  const write = (next: Array<[string, Plain]>) => onChange(Object.fromEntries(next))
  return (
    <div className="space-y-2">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            className={cn(field, 'w-[38%] shrink-0')}
            value={k}
            placeholder={keyHint}
            onChange={(e) => write(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))}
          />
          <input
            className={field}
            value={String(v)}
            placeholder={valueHint}
            onChange={(e) => write(rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)))}
          />
          <button
            type="button"
            aria-label="Remove"
            onClick={() => write(rows.filter((_, j) => j !== i))}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => write([...rows, [rows.some(([k]) => k === '') ? `field${rows.length + 1}` : '', '']])}
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
      >
        <Plus className="size-3.5" /> Add
      </button>
    </div>
  )
}

function Segmented<T extends string>({
  value,
  options,
  onChange
}: {
  value: T
  options: Array<[T, string]>
  onChange: (v: T) => void
}) {
  return (
    <div
      className="grid gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cn(
            'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
            value === key
              ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white'
              : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white'
          )}
        >
          {text}
        </button>
      ))}
    </div>
  )
}

function lines(text?: string) {
  return (text ?? '').split('\n').filter((l) => l.trim() && !l.trim().startsWith('#')).length
}

export function NodeInspector({ node, earlier, readOnly, hookUrl, memory, onChange, onDelete }: Props) {
  const catalog = useCatalog()
  const d = node.data
  const meta = TYPE_META[node.type]
  const Icon = meta.icon
  const set = (patch: Partial<NodeData>) => !readOnly && onChange(patch)
  const vars = (
    <>
      Use <code>{'{{previous}}'}</code> for the step before, <code>{'{{trigger}}'}</code> for what
      started it, <code>{'{{n2.email}}'}</code> for one field of a JSON answer
      {earlier.length > 0 && (
        <>
          , or{' '}
          {earlier.map((n, i) => (
            <span key={n.id}>
              {i > 0 && ', '}
              <code>{`{{${n.id}}}`}</code> ({nodeTitle(n)})
            </span>
          ))}
        </>
      )}
      .
    </>
  )

  const agents = catalog?.agents ?? []
  const actions = (catalog?.actions ?? []).filter((a) => !d.agent || a.agents.includes(d.agent))
  const action = catalog?.actions.find((a) => a.name === d.tool)

  return (
    <fieldset disabled={readOnly} className="space-y-4">
      <div className="flex items-center gap-2.5">
        {(node.type === 'agent' || node.type === 'action') && d.agent ? (
          <AgentAvatar agentKey={d.agent} size="md" />
        ) : (
          <span className="flex size-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            <Icon className="size-4" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            {meta.label} · {node.id}
          </p>
          <p className="truncate font-bold text-neutral-900 dark:text-white">{nodeTitle(node)}</p>
        </div>
      </div>

      {node.type !== 'trigger' && (
        <Field label="Name">
          <input
            className={field}
            value={d.label ?? ''}
            maxLength={120}
            placeholder={meta.label}
            onChange={(e) => set({ label: e.target.value })}
          />
        </Field>
      )}

      {node.type === 'trigger' && (
        <>
          <Field label="What starts it">
            <select
              className={field}
              value={d.kind ?? 'manual'}
              onChange={(e) => set({ kind: e.target.value as NodeData['kind'] })}
            >
              <option value="manual">When you press Run</option>
              <option value="schedule">On a schedule</option>
              <option value="event">When something happens on XOBLACK</option>
              <option value="webhook">When another app calls it</option>
            </select>
          </Field>
          {d.kind === 'webhook' && (
            <div className="rounded-[24px] bg-neutral-100 p-3 dark:bg-neutral-900">
              <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">Web address</p>
              {hookUrl ? (
                <>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <code className="min-w-0 flex-1 truncate text-[11px] text-neutral-600 dark:text-neutral-300">
                      {hookUrl}
                    </code>
                    <button
                      type="button"
                      aria-label="Copy the address"
                      onClick={() =>
                        navigator.clipboard
                          .writeText(hookUrl)
                          .then(() => toast.success('Address copied'))
                          .catch(() => toast.error('Could not copy'))
                      }
                      className="flex size-7 shrink-0 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-white hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white"
                    >
                      <Copy className="size-3.5" />
                    </button>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
                    POST JSON here while the workflow is on. Read it in later steps with{' '}
                    <code>{'{{trigger.field}}'}</code>. Keep the address secret.
                  </p>
                </>
              ) : (
                <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                  Save to get its address.
                </p>
              )}
            </div>
          )}
          {d.kind === 'schedule' && (
            <>
              <Field label="How often">
                <select
                  className={field}
                  value={d.every ?? 'daily'}
                  onChange={(e) => set({ every: e.target.value as NodeData['every'] })}
                >
                  <option value="15m">Every 15 minutes</option>
                  <option value="hourly">Every hour</option>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                </select>
              </Field>
              {d.every === 'weekly' && (
                <Field label="Day">
                  <select
                    className={field}
                    value={typeof d.weekday === 'number' ? d.weekday : 0}
                    onChange={(e) => set({ weekday: Number(e.target.value) })}
                  >
                    {WEEKDAYS.map((day, i) => (
                      <option key={day} value={i}>
                        {day}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {(d.every === 'daily' || d.every === 'weekly' || !d.every) && (
                <Field label="Time" hint="Server time.">
                  <input
                    type="time"
                    className={field}
                    value={d.at_time ?? ''}
                    onChange={(e) => set({ at_time: e.target.value })}
                  />
                </Field>
              )}
            </>
          )}
          {d.kind === 'event' && (
            <Field label="When">
              <select
                className={field}
                value={d.event ?? ''}
                onChange={(e) => set({ event: e.target.value })}
              >
                <option value="">Pick what happens…</option>
                {catalog?.events.map((ev) => (
                  <option key={ev.key} value={ev.key}>
                    {ev.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </>
      )}

      {(node.type === 'agent' || node.type === 'action') && (
        <Field label={node.type === 'agent' ? 'Who does it' : 'Who acts'}>
          <select
            className={field}
            value={d.agent ?? ''}
            onChange={(e) =>
              set(
                node.type === 'action'
                  ? { agent: e.target.value, tool: '', args: {} }
                  : { agent: e.target.value }
              )
            }
          >
            <option value="">Pick a teammate…</option>
            {agents.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
                {a.enabled ? '' : ' (off)'}
              </option>
            ))}
          </select>
        </Field>
      )}

      {node.type === 'agent' && (
        <Field label="What to do" hint={vars}>
          <textarea
            className={cn(field, 'min-h-28 resize-y')}
            value={d.instructions ?? ''}
            placeholder="e.g. Look at the chargeback and the order, and say whether we should fight it."
            onChange={(e) => set({ instructions: e.target.value })}
          />
        </Field>
      )}

      {node.type === 'action' && (
        <>
          <Field
            label="Action"
            hint="Every action becomes an approval card; nothing changes until you say yes."
          >
            <select
              className={field}
              value={d.tool ?? ''}
              disabled={!d.agent}
              onChange={(e) => set({ tool: e.target.value, args: {} })}
            >
              <option value="">{d.agent ? 'Pick an action…' : 'Pick a teammate first'}</option>
              {actions.map((a) => (
                <option key={a.name} value={a.name}>
                  {a.label}
                  {a.money ? ' (money)' : ''}
                </option>
              ))}
            </select>
          </Field>
          {action?.params.map((p) => (
            <Field
              key={p.name}
              label={`${p.name.replace(/_/g, ' ')}${p.required ? ' *' : ''}`}
              hint={p.description}
            >
              {p.enum.length > 0 ? (
                <select
                  className={field}
                  value={String(d.args?.[p.name] ?? '')}
                  onChange={(e) => set({ args: { ...d.args, [p.name]: e.target.value } })}
                >
                  <option value="">Pick…</option>
                  {p.enum.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={field}
                  value={String(d.args?.[p.name] ?? '')}
                  placeholder="Text, or {{previous}}"
                  onChange={(e) => set({ args: { ...d.args, [p.name]: e.target.value } })}
                />
              )}
            </Field>
          ))}
          {action && <p className="text-[11px] leading-relaxed text-neutral-400">{vars}</p>}
        </>
      )}

      {node.type === 'condition' && (
        <>
          <div className="grid grid-cols-2 gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
            {(['rule', 'ai'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => set({ mode })}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                  (d.mode ?? 'rule') === mode
                    ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white'
                    : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white'
                )}
              >
                {mode === 'rule' ? 'Compare' : 'Ask the AI'}
              </button>
            ))}
          </div>
          {d.mode === 'ai' ? (
            <Field label="Yes/no question" hint={vars}>
              <textarea
                className={cn(field, 'min-h-20 resize-y')}
                value={d.question ?? ''}
                placeholder="e.g. Is the customer asking for a refund?"
                onChange={(e) => set({ question: e.target.value })}
              />
            </Field>
          ) : (
            <>
              <Field label="Look at" hint={vars}>
                <input
                  className={field}
                  value={d.value ?? '{{previous}}'}
                  onChange={(e) => set({ value: e.target.value })}
                />
              </Field>
              <Field label="It">
                <select
                  className={field}
                  value={d.op ?? 'contains'}
                  onChange={(e) => set({ op: e.target.value })}
                >
                  {catalog?.ops.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              {d.op !== 'is_empty' && d.op !== 'not_empty' && (
                <Field label="Value" hint="For more/less than, the first number is used ($1,250 reads as 1250).">
                  <input
                    className={field}
                    value={d.compare ?? ''}
                    onChange={(e) => set({ compare: e.target.value })}
                  />
                </Field>
              )}
            </>
          )}
          <p className="text-[11px] text-neutral-400">
            Drag from the Yes and No dots to choose what happens next.
          </p>
        </>
      )}

      {node.type === 'approval' && (
        <>
          <Field label="What you're asked">
            <input
              className={field}
              value={d.title ?? ''}
              maxLength={200}
              placeholder="e.g. Send this to every merchant?"
              onChange={(e) => set({ title: e.target.value })}
            />
          </Field>
          <Field label="Details on the card" hint={vars}>
            <textarea
              className={cn(field, 'min-h-20 resize-y')}
              value={d.message ?? ''}
              onChange={(e) => set({ message: e.target.value })}
            />
          </Field>
        </>
      )}

      {node.type === 'wait' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="How long">
            <input
              type="number"
              min={1}
              className={field}
              value={d.amount ?? ''}
              onChange={(e) => set({ amount: Math.max(0, Number(e.target.value) || 0) })}
            />
          </Field>
          <Field label="Unit">
            <select
              className={field}
              value={d.unit ?? 'hours'}
              onChange={(e) => set({ unit: e.target.value as NodeData['unit'] })}
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </Field>
        </div>
      )}

      {node.type === 'notify' && (
        <>
          <Field label="Message" hint={vars}>
            <textarea
              className={cn(field, 'min-h-24 resize-y')}
              value={d.message ?? ''}
              placeholder="e.g. Chargeback handled: {{previous}}"
              onChange={(e) => set({ message: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              className="size-4 accent-neutral-900 dark:accent-white"
              checked={!!d.email_owner}
              onChange={(e) => set({ email_owner: e.target.checked })}
            />
            Also email it to you
          </label>
        </>
      )}

      {node.type === 'http' && (
        <>
          <div className="grid grid-cols-[96px_1fr] gap-2">
            <Field label="Method">
              <select
                className={field}
                value={d.method ?? 'GET'}
                onChange={(e) => set({ method: e.target.value as NodeData['method'] })}
              >
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Web address">
              <input
                className={field}
                value={d.url ?? ''}
                placeholder="https://api.example.com/orders"
                onChange={(e) => set({ url: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Headers" hint="e.g. Authorization. Stored with the workflow, so use keys made for this.">
            <Pairs
              value={(d.headers ?? {}) as Record<string, Plain>}
              onChange={(headers) => set({ headers: headers as Record<string, string> })}
              keyHint="Name"
              valueHint="Value"
            />
          </Field>
          {d.method && d.method !== 'GET' && (
            <Field label="Body" hint={vars}>
              <textarea
                className={cn(field, 'min-h-24 resize-y font-mono text-xs')}
                value={d.body ?? ''}
                placeholder={'{"email": "{{trigger.email}}"}'}
                onChange={(e) => set({ body: e.target.value })}
              />
            </Field>
          )}
          <p className="text-[11px] leading-relaxed text-neutral-400">
            Only public addresses. The answer is passed on; if it's JSON, later steps can read its fields.
          </p>
        </>
      )}

      {node.type === 'set' && (
        <Field label="Fields" hint={vars}>
          <Pairs
            value={(d.fields ?? {}) as Record<string, Plain>}
            onChange={(fields) => set({ fields })}
            keyHint="name"
            valueHint="{{trigger.email}}"
          />
        </Field>
      )}

      {node.type === 'loop' && (
        <>
          <Field label="Go through" hint="A list from an earlier step, e.g. {{n3}} or {{n3.orders}}. Up to 25 items.">
            <input
              className={field}
              value={d.items ?? '{{previous}}'}
              onChange={(e) => set({ items: e.target.value })}
            />
          </Field>
          <Segmented
            value={(d.mode === 'filter' ? 'filter' : 'map') as 'map' | 'filter'}
            options={[
              ['map', 'Answer for each'],
              ['filter', 'Keep the ones that…']
            ]}
            onChange={(mode) => set({ mode })}
          />
          <Field label={d.mode === 'filter' ? 'Keep an item when' : 'For each item'}>
            <textarea
              className={cn(field, 'min-h-20 resize-y')}
              value={d.instructions ?? ''}
              placeholder={
                d.mode === 'filter'
                  ? 'e.g. the customer spent more than $200'
                  : 'e.g. Write a one-line thank-you using their name.'
              }
              onChange={(e) => set({ instructions: e.target.value })}
            />
          </Field>
        </>
      )}

      {node.type === 'classify' && (
        <>
          {memory && (memory.last.checked !== undefined || memory.stats.right || memory.stats.wrong) && (
            <div className="grid grid-cols-3 gap-2">
              {[
                [
                  'Accuracy',
                  (memory.stats.right ?? 0) + (memory.stats.wrong ?? 0) > 0
                    ? `${Math.round(((memory.stats.right ?? 0) / ((memory.stats.right ?? 0) + (memory.stats.wrong ?? 0))) * 100)}%`
                    : '–',
                  `${(memory.stats.right ?? 0) + (memory.stats.wrong ?? 0)} checked by you`
                ],
                [
                  'Last check',
                  String(memory.last.checked ?? 0),
                  `${memory.last.to_ai ?? 0} to AI · ${memory.last.matched ?? 0} found`
                ],
                ['Cost', `$${(memory.last.cost_usd ?? 0).toFixed(4)}`, 'last check']
              ].map(([label, value, hint]) => (
                <div key={label} className="rounded-[24px] bg-neutral-100 px-3 py-2.5 dark:bg-neutral-900">
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400">{label}</p>
                  <p className="text-lg font-bold tabular-nums text-neutral-900 dark:text-white">{value}</p>
                  <p className="truncate text-[10px] text-neutral-500 dark:text-neutral-400">{hint}</p>
                </div>
              ))}
            </div>
          )}
          {memory?.last.bad_keywords?.length ? (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Skipped (not valid patterns): {memory.last.bad_keywords.join(', ')}
            </p>
          ) : null}
          <Field label="Look at">
            <Segmented
              value={(d.source === 'items' ? 'items' : 'new_posts') as 'new_posts' | 'items'}
              options={[
                ['new_posts', 'New posts'],
                ['items', 'A list from a step']
              ]}
              onChange={(source) => set({ source })}
            />
          </Field>
          {d.source === 'items' && (
            <Field label="The list" hint="e.g. {{n2}} or {{n2.results}}">
              <input
                className={field}
                value={d.items ?? '{{previous}}'}
                onChange={(e) => set({ items: e.target.value })}
              />
            </Field>
          )}
          <Field label="Counts as a match">
            <textarea
              className={cn(field, 'min-h-20 resize-y')}
              value={d.target ?? ''}
              placeholder="e.g. Hate or harassment aimed at Latino people"
              onChange={(e) => set({ target: e.target.value })}
            />
          </Field>
          <Field label="Doesn't count" hint="Look-alikes the AI should ignore.">
            <textarea
              className={cn(field, 'min-h-16 resize-y')}
              value={d.not_target ?? ''}
              placeholder="e.g. Latin music, food, news about places"
              onChange={(e) => set({ not_target: e.target.value })}
            />
          </Field>
          <Field label="How to check">
            <Segmented
              value={(d.mode === 'thorough' ? 'thorough' : 'smart') as 'smart' | 'thorough'}
              options={[
                ['smart', 'Smart (cheap)'],
                ['thorough', 'AI reads everything']
              ]}
              onChange={(mode) => set({ mode })}
            />
          </Field>
          <p className="text-[11px] leading-relaxed text-neutral-400">
            {d.mode === 'thorough'
              ? 'Every item goes to the AI. Best for small lists; costs more on a busy feed.'
              : 'Keywords and closeness to your examples pick what the AI reads; unsure answers get a second, stronger opinion.'}
          </p>
          <Field
            label={`Keywords (${lines(d.keywords)})`}
            hint="One per line. Whole words, plurals and accents handled. /regex/ for patterns, # for notes. Cast a wide net: the AI removes false alarms."
          >
            <textarea
              className={cn(field, 'min-h-28 resize-y font-mono text-xs')}
              value={d.keywords ?? ''}
              onChange={(e) => set({ keywords: e.target.value })}
            />
          </Field>
          <Field
            label={`Example matches (${lines(d.examples)})`}
            hint="One short, realistic post per line. Catches matches that use none of the keywords. Posts you mark Right are added too."
          >
            <textarea
              className={cn(field, 'min-h-24 resize-y text-xs')}
              value={d.examples ?? ''}
              onChange={(e) => set({ examples: e.target.value })}
            />
          </Field>
          <p className="text-[11px] leading-relaxed text-neutral-400">
            The Found path gets the matches as <code>{`{{${node.id}}}`}</code>. Each post is checked once. It
            judges what posts say, never who wrote them.
          </p>
        </>
      )}

      {FALLIBLE.includes(node.type) && (
        <div className="space-y-3 rounded-[24px] border border-neutral-200 p-3 dark:border-neutral-800">
          <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">If it fails</p>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Try again">
              <select
                className={field}
                value={Number(d.retries) || 0}
                onChange={(e) => set({ retries: Number(e.target.value) })}
              >
                <option value={0}>No</option>
                <option value={1}>Once</option>
                <option value={2}>Twice</option>
                <option value={3}>3 times</option>
              </select>
            </Field>
            <Field label="Then">
              <select
                className={field}
                value={d.on_error ?? 'stop'}
                onChange={(e) => set({ on_error: e.target.value as NodeData['on_error'] })}
              >
                <option value="stop">Stop the run</option>
                <option value="continue">Carry on</option>
              </select>
            </Field>
          </div>
          <p className="text-[11px] leading-relaxed text-neutral-400">
            Or drag from the red dot to a fallback step. <code>{'{{error}}'}</code> says what went wrong.
          </p>
        </div>
      )}

      {node.type !== 'trigger' && !readOnly && (
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
        >
          <Trash2 className="size-4" /> Remove step
        </button>
      )}
    </fieldset>
  )
}
